/**
 * My Offerings — the donor's own record.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import { CardHeading, EmptyState, StatusPill } from '../components/ui';
import { api } from '../lib/api';
import { useDonorSession } from '../lib/useDonorSession';
import { formatAmount, formatDate } from '../lib/format';

export default function Offerings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useDonorSession();
  const [orders, setOrders] = useState(null);
  const [lookup, setLookup] = useState('');
  const [lookupError, setLookupError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    api
      .orders(controller.signal)
      .then((o) => setOrders(o.orders))
      .catch(() => setOrders([]));
    return () => controller.abort();
  }, []);

  // Computed here rather than fetched — the v1 order API has no separate
  // aggregate-stats endpoint (the legacy one it replaces did), and this
  // donor's own order list is small enough that a second round trip for it
  // would be waste. `lampsLit` (a lamp-quantity sum) doesn't have a clean
  // source in the current order shape without deeper per-order snapshotting,
  // so it's dropped rather than guessed at from `optionLabel` text.
  const IN_PRAYER_STATES = new Set(['paid', 'accepted', 'performed', 'recorded']);
  const stats = orders && {
    offeringsSent: orders.length,
    inPrayer: orders.filter((o) => IN_PRAYER_STATES.has(o.state)).length,
    completed: orders.filter((o) => o.state === 'completed').length,
  };

  const find = async (event) => {
    event.preventDefault();
    setLookupError('');
    try {
      const { order } = await api.order(lookup.trim());
      navigate(`/confirmation/${order.reference}`);
    } catch (error) {
      setLookupError(error.message);
    }
  };

  return (
    <div className="shell" style={{ paddingBlock: 'var(--space-3xl) var(--space-5xl)' }}>
      <header className="stack" style={{ marginBottom: 'var(--space-2xl)' }}>
        <p className="eyebrow">{t('offerings.eyebrow', 'Your record')}</p>
        <h1 className="display display--lg">{t('offerings.title', 'My offerings')}</h1>
        <p className="lede" style={{ maxWidth: '58ch' }}>
          {t('offerings.lede', 'Every offering you have sent, and where it has reached. An offering shows as In Prayer until the monastery reports the ritual performed.')}
        </p>
      </header>

      {!user && (
        <div className="notice notice--info" style={{ marginBottom: 'var(--space-2xl)' }}>
          <Icon name="people" size={18} />
          <div>
            <strong>{t('offerings.guestBannerTitle', 'These offerings live in this browser')}</strong>
            <span>
              {t(
                'offerings.guestBannerBody',
                'Clear your browser storage and this list goes with it. Sign in and everything here moves to your account permanently.'
              )}
            </span>
          </div>
        </div>
      )}

      {stats && (
        <div className="stat-row" style={{ marginBottom: 'var(--space-3xl)' }}>
          <div className="card stat"><b>{stats.offeringsSent}</b><span>{t('offerings.statSent', 'Offerings sent')}</span></div>
          <div className="card stat"><b>{stats.inPrayer}</b><span>{t('offerings.statInPrayer', 'In prayer')}</span></div>
          <div className="card stat"><b>{stats.completed}</b><span>{t('offerings.statCompleted', 'Completed')}</span></div>
        </div>
      )}

      <div className="detail-layout">
        <div className="stack">
          {orders == null ? (
            <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
          ) : orders.length === 0 ? (
            <EmptyState
              icon="flame"
              title={t('offerings.emptyTitle', 'No offerings yet')}
              action={<Link to="/explore" className="btn btn--primary">{t('offerings.findMonastery', 'Find a monastery')}</Link>}
            >
              {t('offerings.emptyBody', 'When you sponsor a ritual it appears here, with its dedication and its status.')}
            </EmptyState>
          ) : (
            orders.map((order) => (
              <article className="card offering-row" key={order.reference}>
                {order.monastery.photo?.source ? (
                  <img
                    className="offering-row__thumb"
                    src={order.monastery.photo.source}
                    alt={order.monastery.name}
                    loading="lazy"
                  />
                ) : (
                  <span className="offering-row__thumb" style={{ display: 'grid', placeItems: 'center', background: 'var(--primary-light)', color: 'var(--accent-dark)' }}>
                    <Icon name="lotus" size={26} />
                  </span>
                )}

                <div className="stack stack--sm">
                  <div className="row" style={{ gap: 'var(--space-sm)' }}>
                    <StatusPill status={order.status} />
                    <span className="offering-row__ref">{order.reference}</span>
                    <span className="subtle">{formatDate(order.date)}</span>
                  </div>

                  <h2 style={{ fontSize: 16 }}>
                    <Link to={`/confirmation/${order.reference}`} style={{ color: 'inherit' }}>
                      {order.service.name}
                    </Link>
                  </h2>
                  <p className="subtle">
                    {order.optionLabel} · {order.monastery.name}
                  </p>
                  <p className="offering-row__ded">“{order.dedicationText}”</p>
                  {order.dedicationTibetan && (
                    <p className="tibetan" lang="bo" style={{ fontSize: 17, color: 'var(--primary)' }}>
                      {order.dedicationTibetan}
                    </p>
                  )}
                </div>

                <div className="offering-row__amount">{formatAmount(order.amountCents)}</div>
              </article>
            ))
          )}
        </div>

        <aside className="detail-aside">
          <section className="card card__pad">
            <CardHeading icon="search" title={t('offerings.lookupTitle', 'Find by reference')} />
            <p className="field__hint" style={{ marginBottom: 'var(--space-md)' }}>
              {t('offerings.lookupBody', 'Sponsored on another device? Enter your reference number to add it to this list.')}
            </p>
            <form onSubmit={find} className="stack stack--sm">
              <label htmlFor="ref" className="visually-hidden">{t('offerings.refLabel', 'Offering reference')}</label>
              <input
                id="ref"
                className={`input${lookupError ? ' input--invalid' : ''}`}
                value={lookup}
                onChange={(event) => setLookup(event.target.value)}
                placeholder={t('offerings.refPlaceholder', 'GMP-1001')}
                required
              />
              <button className="btn btn--ghost btn--block">{t('offerings.lookupBtn', 'Look it up')}</button>
              {lookupError && <p className="field__error" role="alert"><Icon name="alert" size={14} /> {lookupError}</p>}
            </form>
          </section>
        </aside>
      </div>
    </div>
  );
}
